import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { PropertyPanelInformationTab } from '../../../../PropertyPanelInformationTab'
import { LandInformation } from '../../../../PropertyPanelInformationTab/sections/LandInformation'
import { BuildingsSection } from '../../../../PropertyPanelInformationTab/sections/BuildingsSection'
import { ZonesSection } from '../../../../PropertyPanelInformationTab/sections/ZonesSection'
import { RestrictionsSection } from '../../../../PropertyPanelInformationTab/sections/RestrictionsSection'
import { AnalysisSectionHeader } from '../../../../../AnalysisSectionHeader'
import { useAnalysisMode, useAnalysisServices } from '../../../contexts'
import { useAnalysisMapSectionData } from '../../../contexts/MapSectionDataContext'
import { HeroSection } from './HeroSection'
import { OwnershipCard } from './OwnershipCard'
import { DashboardContainer, DashboardContent } from './SummaryDashboard.styles'

export const SummaryDashboard: React.FC = () => {
  const { t, i18n } = useTranslation()
  const { entity: land, readOnly } = useAnalysisMode()
  const { getSectionData } = useAnalysisServices()
  const { setActiveSection, clearSectionData } = useAnalysisMapSectionData()

  // Clear map section data on mount (summary has no map layers)
  useEffect(() => {
    setActiveSection(null)
    clearSectionData()
  }, [setActiveSection, clearSectionData])

  // Navigation callback (interactive mode only)
  const onNavigate = readOnly
    ? undefined
    : (getSectionData('onNavigateToTab') as ((tab: string) => void) | undefined)

  // Owner portfolio service (interactive mode only)
  const ownerPortfolioService = readOnly
    ? undefined
    : (getSectionData('ownerPortfolioService') as any)

  // RDPPF fetch function (interactive mode only)
  const fetchFullRdppfData = readOnly
    ? undefined
    : (getSectionData('fetchFullRdppfData') as
        | ((landId: string) => Promise<any>)
        | undefined)

  const formatAddress = () => {
    const addresses = (land as any)?.all_addresses as string[] | undefined
    if (addresses?.length) {
      const first = addresses[0]
      const rest = addresses.length - 1
      return rest > 0
        ? `${first} + ${rest} ${t('landAnalysis.summaryTab.otherAddresses')}`
        : first
    }
    if (land?.address_full) return land.address_full
    if (land?.address_street && land?.address_postal_code) {
      return `${land.address_street} ${land.address_street_nb || ''}, ${land.address_postal_code}`.trim()
    }
    return (
      land?.municipality_name || t('landAnalysis.summaryTab.unknownLocation')
    )
  }

  if (!land) {
    return (
      <DashboardContainer>
        <div style={{ textAlign: 'center', padding: '48px', color: '#ff3b30' }}>
          {t('landAnalysis.summaryTab.noData')}
        </div>
      </DashboardContainer>
    )
  }

  return (
    <DashboardContainer>
      <AnalysisSectionHeader
        title={t('landAnalysis.summaryTab.title')}
        subtitle={t('landAnalysis.summaryTab.subtitle', {
          address: formatAddress(),
        })}
      />
      <DashboardContent>
        <HeroSection land={land} onNavigate={onNavigate} />
        <PropertyPanelInformationTab>
          <LandInformation land={land} />
          <BuildingsSection buildings={land.buildings || []} />
          <ZonesSection
            generalPlans={land.land_use_plans_general}
            cantonalPlans={land.land_use_plans_cantonal}
            localPlans={land.land_use_plans_local}
            partialPlans={land.land_use_plans_partial}
          />
          <RestrictionsSection
            rdppfs={land.rdppfs || []}
            landId={land.id}
            fetchFullRdppfData={fetchFullRdppfData}
          />
          <OwnershipCard
            land={land}
            readOnly={readOnly}
            ownerPortfolioService={ownerPortfolioService}
            language={i18n.language}
          />
        </PropertyPanelInformationTab>
      </DashboardContent>
    </DashboardContainer>
  )
}
